/**
 * Parse a run of concatenated 2-character state / DAT-zone codes typed into an
 * Origin / Destination box — `"GAFL"` → GA + FL, `"FLGAIL"` → FL + GA + IL,
 * `"Z0Z1Z2"` → three zones.
 *
 * Two rules make this safe enough to fire on ENTER:
 *
 *   1. **Unrecognised pairs are skipped, not fatal.** `"GAFLXX"` yields GA + FL.
 *      A trailing odd character is dropped the same way. This is what the
 *      dispatcher asks for when one code in a long run is a typo — they get the
 *      states that ARE real instead of nothing at all.
 *   2. **At least {@link MIN_CODES} codes must resolve.** A four-letter city
 *      whose first half happens to be a state ("LAS Vegas" → LA + SV) yields one
 *      code and is rejected, so the caller falls through to the normal city
 *      search. Two-plus codes is the point where "this is a list of states"
 *      stops being a coincidence.
 *
 * Rule 2 alone does not settle every case — "WACO" is WA + CO and also a real
 * city — so the caller is expected to reject the run when the typed text
 * prefix-matches a city it is already showing. See `LocationSearch`.
 *
 * Generic over the match type so the parser stays free of the location dataset
 * and is unit-testable with a plain lookup table.
 */

/**
 * How many codes must resolve before input is treated as a state list rather than a city.
 * Shared with the delimited path in `LocationSearch` so both forms agree on the threshold.
 */
export const MIN_CODES = 2

const CODE_LENGTH = 2

/** Only letters and digits can be a state ("GA") or zone ("Z0") code — never a ZIP or free text. */
const CODE_RUN_PATTERN = /^[A-Z0-9]+$/

/**
 * Split `token` into 2-character codes and resolve each through `matchCode`.
 * Returns the resolved codes, or `null` when the run doesn't look like a state
 * list at all (non-alphanumeric, too short, or fewer than {@link MIN_CODES} hits).
 *
 * @param token   Raw input, already trimmed. Upper-cased internally.
 * @param matchCode Resolves one 2-char code, or returns null when unknown.
 */
export const parseCodeRun = <T>(token: string, matchCode: (code: string) => T | null): T[] | null => {
    const normalized = token.toUpperCase()
    if (normalized.length < CODE_LENGTH * MIN_CODES) return null
    if (!CODE_RUN_PATTERN.test(normalized)) return null

    const matches: T[] = []
    // Fixed stride: a state code is exactly two characters, so a stray character
    // shifts nothing — it just costs the pair it sits in. Re-aligning the window
    // would let "CHICAGO" resolve some interior pair and invent a state.
    for (let i = 0; i + CODE_LENGTH <= normalized.length; i += CODE_LENGTH) {
        const match = matchCode(normalized.slice(i, i + CODE_LENGTH))
        if (match) matches.push(match)
    }

    return matches.length >= MIN_CODES ? matches : null
}
